import {
  ExternalLink as Open16Regular,
  LogOut as SignOut20Regular,
  Clock as Clock16Regular,
} from "lucide-react";
import { useState, lazy, Suspense } from "react";
import { useNavigate } from "react-router";
import {
  SkyAvatar,
  SkyButton,
  SkyLoader,
  SkyPopover,
  SkySkeleton,
  SkySkeletonItem,
  SkyText,
} from "@skypointcloud/skypoint-ui";

import { useCurrentUser } from "../hooks";
import { usePacks } from "../../../../components/packs/PackContext";
import { ROUTES } from "../../../../components/shared/constant";
import { getInstanceIdFromUrl } from "../../../../utils/instanceId";
import { TenantInstanceSwitcher } from "./TenantInstanceSwitcher";
import { formatInstant } from "../../../../common/utilities/displayTime";
import { useDisplayTz } from "../../../../hooks/useDisplayTz";
import { useActiveDisplayTz } from "../../../../hooks/useActiveDisplayTz";
import { displayTimezoneLabel } from "../../../../common/constants/displayTimezones";

// Lazy-load dialogs - only loaded when user opens them
const FeedbackDialog = lazy(() =>
  import("./FeedbackDialog").then((m) => ({ default: m.FeedbackDialog })),
);
const TimezoneDialog = lazy(() =>
  import("../../../../components/TimezoneDialog").then((m) => ({ default: m.TimezoneDialog })),
);

interface ProfileMenuProps {
  onSignOut: () => void;
}

export function ProfileMenu({ onSignOut }: ProfileMenuProps) {
  // Get user data from hook
  const { currentUser, initials, isLoaded: isUserLoaded } = useCurrentUser();
  const navigate = useNavigate();
  const { hasFeature } = usePacks();
  // "View profile" opens the shared self-serve My Profile surface. Keyed to the
  // "user_profile" pack capability (Workstream B, STATUS-advisor-view-profile.md
  // D-VP-4) — NOT to entry site or view mode — so advisor (lead_gen_only) users
  // whose pack declares the feature can reach it, and a dual-pack user sees the
  // link from either view.
  const showProfileLink = hasFeature("user_profile");

  // Dialog states
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showTzDialog, setShowTzDialog] = useState(false);

  // Context change loading state (for tenant/instance switch)
  const [isChangingContext, setIsChangingContext] = useState(false);

  // Menu state - Fluent UI Popover handles click-outside automatically including nested portals
  const [menuOpen, setMenuOpen] = useState(false);

  // lastLogin is an account/audit timestamp — a tz-NO surface, deliberately still on the PERSONAL
  // chain (see the sweep table in STATUS-tour-display-timezone.md). It is NOT switched to the
  // effective zone: that would be a per-surface widening the #921 ruling did not make.
  const zone = useDisplayTz();
  // The Preferences row is an INDICATOR, so it must report the zone the app is ACTUALLY rendering
  // in — the whole point of the control. It used to show useDisplayTz() (personal) while
  // community-mode surfaces rendered in the community's zone, i.e. the one affordance meant to
  // prevent a misread was itself the misread (#921 ruling item 2, 2026-08-17).
  const { zone: effectiveZone, source, communityName } = useActiveDisplayTz();
  const name = currentUser?.fullname || "";
  const email = (currentUser?.email as string) || "";
  const lastLogin = currentUser?.lastLogin
    ? formatInstant(currentUser.lastLogin as string, zone)
    : null;

  return (
    <>
      <div className="relative z-[1030] max-h-[100px]">
        {/* Trigger */}
        {!isUserLoaded ? (
          <SkySkeleton
            aria-label="Loading user"
            className="flex items-center gap-2"
          >
            <SkySkeletonItem
              shape="circle"
              className="w-8! h-8!"
            />
            <SkySkeletonItem className="w-24! h-4! hidden! md:block!" />
          </SkySkeleton>
        ) : (
          <SkyPopover
            open={menuOpen}
            onOpenChange={setMenuOpen}
            placement="bottom-end"
            // The panel is TALLER than the space under the app header (identity,
            // tenant, instance, preferences, four links, Logout, version). The
            // popover's positioner answers that by sliding the whole panel up
            // until its bottom fits the viewport — which pushed its first row,
            // the avatar and the signed-in name, up behind the header where it
            // could not be read or reached. Capping the height instead means the
            // panel never needs to move: it opens under the trigger and scrolls
            // internally, so the identity row stays the first thing on screen.
            className="flex max-h-[calc(100vh-80px)] flex-col overflow-hidden"
            trigger={
              <div
                className="flex items-center gap-2 cursor-pointer"
                role="button"
                tabIndex={0}
                onClick={() => setMenuOpen((v) => !v)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setMenuOpen((v) => !v);
                  }
                }}
              >
                <SkyAvatar
                  name={initials}
                  size={32}
                  className="hover:ring-2 hover:ring-white/30 transition-all"
                  color="brand"
                />
                {name && (
                  <SkyText
                    size={400}
                    weight="medium"
                    className="text-white hidden! md:block!"
                  >
                    {name}
                  </SkyText>
                )}
              </div>
            }
          >
            <div className="flex min-h-0 min-w-75 flex-1 flex-col">
              {/* Profile Header — pinned. Who you are signed in as is the first
                  question the menu answers, so it does not scroll away. */}
              <div className="flex shrink-0 items-start gap-3 p-4 border-b border-gray-200">
                <SkyAvatar
                  name={initials}
                  size={40}
                  color="brand"
                />
                <div className="flex flex-col min-w-0 flex-1">
                  <SkyText
                    weight="semibold"
                    className="text-gray-900 truncate"
                    size={400}
                  >
                    {name}
                  </SkyText>
                  {email && (
                    <SkyText
                      size={300}
                      className="text-gray-500 truncate"
                    >
                      {email}
                    </SkyText>
                  )}
                  {showProfileLink && (
                    <button
                      type="button"
                      onClick={() => {
                        const id = getInstanceIdFromUrl() ?? "";
                        navigate(`${ROUTES.CRM_PROFILE}/${id}`);
                        setMenuOpen(false);
                      }}
                      className="mt-1 w-fit p-0 text-left text-sm font-medium underline-offset-2 hover:underline"
                    >
                      View profile
                    </button>
                  )}
                  {lastLogin && (
                    <SkyText
                      size={200}
                      className="text-gray-400 mt-1"
                    >
                      Last login: {lastLogin}
                    </SkyText>
                  )}
                </div>
              </div>

              {/* Everything between the identity row and Logout scrolls. Capping
                  the whole panel (so it stops sliding up behind the header) left
                  Logout half-drawn at the cut on a short viewport — a primary
                  action sliced in two reads as broken. Scrolling the MIDDLE keeps
                  both ends whole at any height. */}
              <div className="min-h-0 flex-1 overflow-y-auto">
                {/* Tenant/Instance Switcher */}
                <TenantInstanceSwitcher
                  onContextChange={() => setMenuOpen(false)}
                  onLoadingChange={setIsChangingContext}
                />

                {/* Preferences group: the account display timezone. Kept separate from the
                  external resource links below by its own label + divider so it reads as a
                  setting, not another link. */}
                <div className="px-4 pt-2 pb-1">
                  <SkyText
                    size={100}
                    className="text-gray-400 uppercase tracking-wide font-semibold"
                  >
                    Preferences
                  </SkyText>
                </div>
                <div className="flex flex-col">
                  <SkyButton
                    appearance="transparent"
                    className="justify-between! px-4!"
                    onClick={() => {
                      setShowTzDialog(true);
                      setMenuOpen(false);
                    }}
                  >
                    <span className="flex items-center gap-2">
                      <Clock16Regular className="text-amber-500" />
                      Time zone
                    </span>
                    <span className="text-gray-500 text-sm">
                      {displayTimezoneLabel(effectiveZone)}
                    </span>
                  </SkyButton>
                  {/* Naming the SOURCE is what makes the two confusing states legible: community
                    mode showing a community's clock, and community mode + "All communities"
                    silently falling back to the personal one. */}
                  <SkyText
                    size={100}
                    className="px-4 pb-2 text-gray-400"
                  >
                    {source === "community"
                      ? `Following ${communityName ?? "the selected community"}`
                      : "Your personal time zone"}
                  </SkyText>
                </div>

                <div className="mx-4 my-1 border-t border-gray-100" />

                {/* Resources */}
                <div className="py-1 flex flex-col">
                  <SkyButton
                    appearance="transparent"
                    className="justify-between! px-4!"
                    onClick={() => {
                      // "noopener" is NOT implied here. An <a target="_blank"> gets
                      // it for free in modern browsers; `window.open` does not, so
                      // without it the opened page keeps a live `window.opener`
                      // handle on this one and can navigate it (reverse tabnabbing).
                      // These three are our own domains, which is why this was never
                      // urgent — but the argument costs nothing and the guarantee
                      // stops depending on who owns the destination.
                      window.open("https://docs.skypointcloud.com/docs/intro", "_blank", "noopener");
                      setMenuOpen(false);
                    }}
                  >
                    Documentation
                    <Open16Regular className="text-gray-400" />
                  </SkyButton>

                  <SkyButton
                    appearance="transparent"
                    className="justify-between! px-4!"
                    onClick={() => {
                      window.open("https://privacy.skypoint.ai/", "_blank", "noopener");
                      setMenuOpen(false);
                    }}
                  >
                    Privacy Portal
                    <Open16Regular className="text-gray-400" />
                  </SkyButton>

                  <SkyButton
                    appearance="transparent"
                    className="justify-between! px-4!"
                    onClick={() => {
                      window.open(
                        "https://skypoint.ai/master-services-agreement-8-7-2025/",
                        "_blank",
                        "noopener",
                      );
                      setMenuOpen(false);
                    }}
                  >
                    Terms of Use
                    <Open16Regular className="text-gray-400" />
                  </SkyButton>

                  <SkyButton
                    appearance="transparent"
                    className="justify-start! px-4!"
                    onClick={() => {
                      setShowFeedbackModal(true);
                      setMenuOpen(false);
                    }}
                  >
                    Feedback
                  </SkyButton>
                </div>
              </div>

              {/* Logout & Version — pinned, always whole. */}
              <div className="shrink-0 p-4 pt-2 flex flex-col gap-2 border-t border-gray-200 bg-white">
                <SkyButton
                  appearance="primary"
                  icon={SignOut20Regular}
                  onClick={() => {
                    onSignOut();
                    setMenuOpen(false);
                  }}
                  className="w-full"
                >
                  Logout
                </SkyButton>
                <SkyText
                  size={200}
                  className="text-gray-400 text-center"
                >
                  Version {__APP_VERSION__}
                </SkyText>
              </div>
            </div>
          </SkyPopover>
        )}
      </div>

      {/* Feedback Dialog - lazy loaded */}
      {showFeedbackModal && (
        <Suspense fallback={null}>
          <FeedbackDialog
            open={showFeedbackModal}
            onOpenChange={setShowFeedbackModal}
          />
        </Suspense>
      )}

      {/* Account timezone picker - lazy loaded */}
      {showTzDialog && (
        <Suspense fallback={null}>
          <TimezoneDialog
            open={showTzDialog}
            onClose={() => setShowTzDialog(false)}
            instanceId={getInstanceIdFromUrl() ?? undefined}
          />
        </Suspense>
      )}

      {/* Blocking overlay during tenant/instance context change */}
      {isChangingContext && (
        <div className="fixed inset-0 z-99999 bg-white flex items-center justify-center">
          <SkyLoader
            size="large"
            label="Loading..."
          />
        </div>
      )}
    </>
  );
}
